import { BackgroundCheckConfig, BackgroundCheckConfigByType } from '@/backend/features/background-check/defaults';
import { BackgroundCheckType, intellicorpBackgroundCheckTypes } from '@/backend/features/background-check/types';
import { SettingData } from '@juvare/platform-sdk-react/backend/features/editable-settings/shared';

const changedBackgroundCheckConfigTypes = (
    oldSettingData: SettingData,
    newSettingData: SettingData,
    valueKey: (keyof BackgroundCheckConfigByType)[],
): BackgroundCheckType[] => {
    if (oldSettingData.name === 'backgroundCheckConfig' && newSettingData.name === 'backgroundCheckConfig') {
        const oldValue: BackgroundCheckConfig | undefined = oldSettingData?.value ? JSON.parse(oldSettingData.value) : undefined;
        const newValue: BackgroundCheckConfig | undefined = newSettingData?.value ? JSON.parse(newSettingData.value) : undefined;

        if ((!oldValue?.type && !newValue?.type) || !valueKey.length) {
            return [];
        } else if (!oldValue?.type || !newValue?.type) {
            return Object.keys(intellicorpBackgroundCheckTypes) as BackgroundCheckType[];
        }

        const newTypes = Object.entries(newValue.type)
            .filter(([type, config]) => {
                return !oldValue.type[type as BackgroundCheckType] && !!config?.enabled;
            })
            .map(([type]) => type as BackgroundCheckType);

        const updatedTypes = Object.entries(oldValue.type)
            .filter(([type, backgroundCheckConfigByType]) => {
                return valueKey.some((key) => {
                    return (
                        backgroundCheckConfigByType?.[key] !== newValue.type[type as BackgroundCheckType]?.[key] &&
                        !!newValue?.type[type as BackgroundCheckType]?.enabled
                    );
                });
            })
            .map(([type]) => type as BackgroundCheckType);

        const affectedTypes = [...updatedTypes, ...newTypes];

        return affectedTypes;
    }

    return [];
};

export function isBackgroundCheckConfigUpdated(previousData: SettingData, data: SettingData): BackgroundCheckType[] {
    return changedBackgroundCheckConfigTypes(previousData, data, ['enabled', 'process', 'requiresFormValidation', 'requiresConsent']);
}

export const getBackgroundCheckConfigSettingProcessValue = (singleSettingData: SettingData, type: BackgroundCheckType) => {
    const value: BackgroundCheckConfig | undefined = singleSettingData?.value ? JSON.parse(singleSettingData.value) : undefined;

    return value?.type[type as BackgroundCheckType]?.process;
};

export const getBackgroundCheckConfigSettingRequiresFormValidationValue = (singleSettingData: SettingData, type: BackgroundCheckType) => {
    const value: BackgroundCheckConfig | undefined = singleSettingData?.value ? JSON.parse(singleSettingData.value) : undefined;

    return value?.type[type as BackgroundCheckType]?.requiresFormValidation;
};

const getBackgroundCheckConfigSettingManualValue = (singleSettingData: SettingData): BackgroundCheckType | undefined => {
    if (singleSettingData.name === 'backgroundCheckConfig') {
        const value: BackgroundCheckConfig | undefined = singleSettingData?.value ? JSON.parse(singleSettingData.value) : undefined;

        const type = value ? Object.entries(value.type).find(([, value]) => value?.enabled)?.[0] : undefined;

        return type as BackgroundCheckType | undefined;
    }

    return undefined;
};

export function backgroundCheckTypeChanges(
    previousData: SettingData,
    data: SettingData,
): {
    previousBackgroundCheckType: BackgroundCheckType | undefined;
    newBackgroundCheckType: BackgroundCheckType | undefined;
} {
    const previousBackgroundCheckType = getBackgroundCheckConfigSettingManualValue(previousData);
    const newBackgroundCheckType = getBackgroundCheckConfigSettingManualValue(data);

    const backgroundCheckTypeChanged = previousBackgroundCheckType !== newBackgroundCheckType;

    if (!backgroundCheckTypeChanged) {
        return { previousBackgroundCheckType: undefined, newBackgroundCheckType: undefined };
    }

    return { previousBackgroundCheckType, newBackgroundCheckType };
}
